import {
  ArdoqId,
  DashboardFilterValues,
  HexColor,
  WidgetTypes,
} from '@ardoq/api-types';
import {
  DashboardFilterParameter,
  DashboardFiltersCommands,
  DashboardModes,
  TrackEvent,
  WidgetsCommands,
} from '../types';
import {
  DashboardBuilderChartWidget,
  DashboardBuilderChartWidgetProps,
  DashboardBuilderWidgetsProps,
} from './types';
import { widgetOperations } from './operations';
import { getEditModeHeaderWidgetProps } from './HeaderWidget/getHeaderWidgetProps';
import { getEditModePowerBiIframeWidgetProps } from './PowerBiIframeWidget/getPowerBiIframeWidgetProps';
import { getEditModeNoteWidgetProps } from './NoteWidget/getNoteWidgetProps';
import { Locale } from '@ardoq/locale';
import { EditModeWidgetProps } from './editModeTypes';
import { GlobalFiltersWidgetMaskProps } from './atoms/GlobalFiltersWidgetMask';
import {
  GetComponentLinkHandler,
  HasComponentReadPermission,
} from '@ardoq/report-reader';
import { EnhancedScopeData } from '@ardoq/data-model';
import { isEmpty } from 'lodash';
import { getEditModeChartWidgetHeaderBarProps } from './atoms/getEditModeChartWidgetHeaderBarProps';
import { getCommonWidgetProps } from './getCommonWidgetProps';
import { EditModeChartWidgetProps } from './widgetPropTypes';
import { type I18nLocale, t } from '@ardoq/i18n';
import { getTranslatedWidgetError } from './getTranslatedWidgetError';

const getCannotEditMessage = (
  widget: DashboardBuilderChartWidget,
  hasDashboardFilterValues: boolean,
  i18nLocale: I18nLocale
): string => {
  const translatedWidgetError = getTranslatedWidgetError(widget, i18nLocale);
  if (translatedWidgetError) return translatedWidgetError;

  if (hasDashboardFilterValues)
    return t(
      i18nLocale,
      'dashboard:editMode.clearFiltersToEdit',
      'Clear dashboard filters to edit this chart'
    );

  const sourceType = widget.datasource?.sourceType || 'asset';
  return t(
    i18nLocale,
    'dashboard:editMode.noAccessToSource',
    { sourceType },
    "You don't have access to this chart's {{sourceType}}"
  );
};

const prepareGlobalFiltersWidgetMaskProps = (
  widgetData: DashboardBuilderChartWidget,
  linkModeDashboardFilterParameter: DashboardFilterParameter,
  isLinkToLinkModeDashboardFilterSupported: boolean,
  isLinkedToLinkModeDashboardFilter: boolean,
  isLinkModeDashboardFilterFieldUsedInWidgetFilter: boolean,
  isLinkModeDashboardFilterFieldUsedInWidgetCondition: boolean
): GlobalFiltersWidgetMaskProps => ({
  isWide: widgetData.layout.w > 1,
  isLinkToLinkModeDashboardFilterSupported,
  isLinkedToLinkModeDashboardFilter,
  isWarning:
    (isLinkToLinkModeDashboardFilterSupported &&
      isLinkModeDashboardFilterFieldUsedInWidgetFilter) ||
    (!isLinkToLinkModeDashboardFilterSupported &&
      isLinkedToLinkModeDashboardFilter),
  maskMessage: widgetOperations.getWidgetMaskMessageForDashboardFilters({
    widgetData,
    linkModeDashboardFilterParameter,
    isLinkToLinkModeDashboardFilterSupported,
    isLinkedToLinkModeDashboardFilter,
    isLinkModeDashboardFilterFieldUsedInWidgetFilter,
    isLinkModeDashboardFilterFieldUsedInWidgetCondition,
    datasourceStrategy: widgetData.datasourceStrategy,
  }),
});

const getEditModeChartWidgetProps = ({
  dashboardId,
  widgetData,
  userLocale,
  colors,
  getHasComponentReadPermission,
  getComponentLinkHandler,
  isSelected,
  isInLinkDashboardFiltersMode,
  linkModeDashboardFilterParameterId,
  commands,
  trackEvent,
  isSaving,
  dashboardFilterValues,
  dashboardFilterParameters,
  i18nLocale,
}: {
  dashboardId: ArdoqId | undefined;
  userLocale: Locale;
  colors: Record<string, HexColor>;
  isSelected: boolean;
  i18nLocale: I18nLocale;
  getComponentLinkHandler?: GetComponentLinkHandler;
  getHasComponentReadPermission?: (
    scopeData: EnhancedScopeData
  ) => HasComponentReadPermission;
  dashboardFilterValues: DashboardFilterValues;
  dashboardFilterParameters: DashboardFilterParameter[];
  widgetData: DashboardBuilderChartWidgetProps;
  isSaving: boolean;
  trackEvent: TrackEvent;
  isInLinkDashboardFiltersMode: boolean;
  linkModeDashboardFilterParameterId: string | null;
  commands: WidgetsCommands & {
    editWidgetLinkModeDashboardFilterBinding: (widgetId: ArdoqId) => void;
    unlinkWidgetWithDashboardFilter: (
      widgetId: ArdoqId,
      dashboardFilterParameterId: ArdoqId
    ) => void;
    linkWidgetWithDashboardFilter: (
      widgetId: ArdoqId,
      dashboardFilterParameterId: ArdoqId
    ) => void;
  };
}): EditModeChartWidgetProps => {
  const {
    configurationInstructions,
    viewValidationErrors,
    dashboardFilterSupportedFields,
    isLoading,
  } = widgetData;

  const linkModeDashboardFilterParameter = dashboardFilterParameters.find(
    param => param.id === linkModeDashboardFilterParameterId
  );

  const {
    isLinkToLinkModeDashboardFilterSupported,
    isLinkModeDashboardFilterFieldUsedInWidgetFilter,
    isLinkedToLinkModeDashboardFilter,
    isLinkModeDashboardFilterFieldUsedInWidgetCondition,
  } = widgetOperations.getWidgetLinkModeDashboardFilterProps(
    widgetData,
    linkModeDashboardFilterParameter,
    dashboardFilterSupportedFields ?? []
  );
  const confidentialFieldLabelsByName =
    'confidentialFieldLabelsByName' in widgetData &&
    widgetData.confidentialFieldLabelsByName
      ? widgetData.confidentialFieldLabelsByName
      : {};

  return {
    ...getCommonWidgetProps({
      viewValidationErrors: viewValidationErrors ?? [],
      dashboardId,
      isLoading: Boolean(isLoading),
      configurationInstructions,
      widgetData,
      userLocale,
      dashboardMode: DashboardModes.EDIT,
      colors,
      onDrilldown: undefined, // no drilldown in edit mode
      getHasComponentReadPermission,
      getComponentLinkHandler,
      isSelected,
      dashboardFilterValues,
      dashboardFilterParameters,
      confidentialFieldLabelsByName,
      i18nLocale,
    }),
    configurationInstructions: configurationInstructions ?? [],
    headerBarProps: getEditModeChartWidgetHeaderBarProps({
      name: widgetData.name,
      widgetId: widgetData._id,
      widgetType: widgetData.viewType as Exclude<
        WidgetTypes,
        WidgetTypes.HEADER | WidgetTypes.NOTE
      >, // todo narrow types to exclude header and note higher in props
      canEditWidget: widgetData.permissions?.canEditWidget ?? false,
      isEditDisabled: !isEmpty(dashboardFilterValues),
      cannotEditMessage: getCannotEditMessage(
        widgetData,
        !isEmpty(dashboardFilterValues),
        i18nLocale
      ),
      linkModeProps: {
        isInLinkDashboardFiltersMode,
        linkModeDashboardFilterParameter,
        isLinkToLinkModeDashboardFilterSupported,
        isLinkModeDashboardFilterFieldUsedInWidgetFilter,
        isLinkedToLinkModeDashboardFilter,
        isLinkModeDashboardFilterFieldUsedInWidgetCondition,
      },
      commands,
      isSaving,
      trackEvent,
      confidentialFieldLabels: Object.values(confidentialFieldLabelsByName),
      linkedDashboardFilters: widgetOperations.getWidgetLinkedDashboardFilters({
        widgetData,
        globalFilterValues: dashboardFilterValues,
        dashboardFilterParameters,
      }),
      i18nLocale,
    }),
    dashboardMode: DashboardModes.EDIT,
    isInLinkDashboardFiltersMode,
    globalFiltersWidgetMaskProps:
      isInLinkDashboardFiltersMode && linkModeDashboardFilterParameter
        ? prepareGlobalFiltersWidgetMaskProps(
            widgetData,
            linkModeDashboardFilterParameter,
            isLinkToLinkModeDashboardFilterSupported,
            isLinkedToLinkModeDashboardFilter,
            isLinkModeDashboardFilterFieldUsedInWidgetFilter,
            isLinkModeDashboardFilterFieldUsedInWidgetCondition
          )
        : undefined,
    isLoading: Boolean(isLoading),
  };
};

export const getEditModeWidgetProps = ({
  dashboardId,
  widget,
  dashboardFilterParameters,
  dashboardFilterValues,
  selectedWidgetId,
  isInLinkDashboardFiltersMode,
  linkModeDashboardFilterParameterId,
  widgetsCommands,
  isSaving,
  trackEvent,
  userLocale,
  colors,
  dashboardFilterCommands,
  i18nLocale,
}: {
  dashboardId: ArdoqId | undefined;
  widget: DashboardBuilderWidgetsProps;
  dashboardFilterValues: DashboardFilterValues;
  dashboardFilterParameters: DashboardFilterParameter[];
  selectedWidgetId: string | null;
  isInLinkDashboardFiltersMode: boolean;
  widgetsCommands: WidgetsCommands;
  linkModeDashboardFilterParameterId: string | null;
  isSaving: boolean;
  trackEvent: TrackEvent;
  userLocale: Locale;
  colors: Record<string, HexColor>;
  dashboardFilterCommands: DashboardFiltersCommands;
  i18nLocale: I18nLocale;
}): EditModeWidgetProps => {
  return widgetOperations.isHeaderWidgetData(widget)
    ? {
        ...getEditModeHeaderWidgetProps({
          widgetData: widget,
          isInLinkDashboardFiltersMode,
          selectedWidgetId,
          isSaving,
          trackEvent,
          commands: widgetsCommands,
        }),
        type: 'header',
      }
    : widgetOperations.isNoteWidgetData(widget)
      ? {
          ...getEditModeNoteWidgetProps({
            widgetData: widget,
            selectedWidgetId,
            isInLinkDashboardFiltersMode,
            commands: widgetsCommands,
            isSaving,
            trackEvent,
            i18nLocale,
          }),
          type: 'note',
        }
      : widgetOperations.isPowerBiIframeWidgetData(widget)
        ? {
            ...getEditModePowerBiIframeWidgetProps({
              widgetData: widget,
              selectedWidgetId,
              isInLinkDashboardFiltersMode,
              commands: widgetsCommands,
              isSaving,
              trackEvent,
              i18nLocale,
            }),
            type: 'powerBiIframe',
          }
        : {
            ...getEditModeChartWidgetProps({
              dashboardId,
              widgetData: widget,
              dashboardFilterValues,
              dashboardFilterParameters,
              isInLinkDashboardFiltersMode,
              isSelected: selectedWidgetId === widget._id,
              linkModeDashboardFilterParameterId,
              commands: {
                ...widgetsCommands,
                ...dashboardFilterCommands,
              },
              isSaving,
              trackEvent,
              userLocale,
              colors,
              i18nLocale,
            }),
            type: 'chart',
          };
};
